// BootstrapService — the section presets + fetch/map pipeline (contract 07 §8). Ported from
// v3; the `disableBootstrapForSession` dead-man's-switch and its English string-sniff on
// `error.response.data.error` are STRIPPED (v4 has one boot path, no flag, and branches on
// `ApiError.code`/`kind` — never on prose). The section presets are preserved verbatim.
import { BootstrapApi } from '../api/BootstrapApi'
import type { BootstrapSection, MappedBootstrapResult } from '../models/bootstrap.types'

import { mapBootstrapResponse } from './BootstrapMapper'

// `rbac` (BE-ACC-01) rides every ACCOUNT-level preset: it is the authoritative `isSysAdmin` +
// permission source, needs NO `workspace_id` (only its `flags` fill when one is present), and is
// mostly account-scoped, so the workspace-only presets below deliberately do not re-request it — a
// workspace switch keeps warm account RBAC but clears workspace-scoped capabilities. The
// BE-ACC-03/-05 nav sections split the same
// way: `reports`/`schedules`/`integrations_connected`/`dashboard_access` are account/user-scoped
// and ride the account-level presets (one boot call hydrates the sidebar), while `feature_flags`
// REQUIRES a `workspace_id` (backend `WORKSPACE_REQUIRED_SECTIONS`) and rides ONLY the
// workspace-scoped presets — adding it to an account-level preset would 400 the whole boot.
/** The account/user-scoped nav sections (BE-ACC-03 / BE-ACC-05) every account-level boot carries. */
const ACCOUNT_NAV_SECTIONS: BootstrapSection[] = [
  'reports',
  'schedules',
  'integrations_connected',
  'dashboard_access',
]

const ALL_SECTIONS: BootstrapSection[] = [
  'account_core',
  'account_detail',
  'workspace_core',
  'dimensions',
  'compositions',
  'integrations',
  'settings',
  'session_access',
  'rbac',
  ...ACCOUNT_NAV_SECTIONS,
  'feature_flags',
]

const ACCOUNT_ONLY_SECTIONS: BootstrapSection[] = [
  'account_core',
  'account_detail',
  'session_access',
  'rbac',
  ...ACCOUNT_NAV_SECTIONS,
]

const WORKSPACE_SWITCH_SECTIONS: BootstrapSection[] = [
  'workspace_core',
  'dimensions',
  'compositions',
  'integrations',
  'settings',
  'feature_flags',
]

const INITIAL_WORKSPACE_SECTIONS: BootstrapSection[] = [
  'workspace_core',
  'dimensions',
  'integrations',
  'settings',
  'feature_flags',
]

const INITIAL_WORKSPACE_WITH_COMPOSITIONS_SECTIONS: BootstrapSection[] = [
  ...INITIAL_WORKSPACE_SECTIONS,
  'compositions',
]

const INITIAL_FULL_SECTIONS: BootstrapSection[] = [
  'account_core',
  'account_detail',
  'session_access',
  'rbac',
  ...ACCOUNT_NAV_SECTIONS,
  ...INITIAL_WORKSPACE_SECTIONS,
]

const INITIAL_FULL_WITH_COMPOSITIONS_SECTIONS: BootstrapSection[] = [
  ...INITIAL_FULL_SECTIONS,
  'compositions',
]

interface BootstrapFetchOptions {
  refresh?: boolean
  cursor?: string
  scheduleId?: string
}

export class BootstrapService {
  constructor(private readonly api: BootstrapApi = new BootstrapApi()) {}

  public async bootFull(
    accountId: number,
    workspaceId: number | string,
    options?: BootstrapFetchOptions,
  ): Promise<MappedBootstrapResult> {
    return this.fetch(accountId, ALL_SECTIONS, workspaceId, options)
  }

  public async bootAccountOnly(
    accountId: number,
    options?: BootstrapFetchOptions,
  ): Promise<MappedBootstrapResult> {
    return this.fetch(accountId, ACCOUNT_ONLY_SECTIONS, undefined, options)
  }

  public async bootWorkspaceSwitch(
    accountId: number,
    workspaceId: number | string,
    options?: BootstrapFetchOptions,
  ): Promise<MappedBootstrapResult> {
    return this.fetch(accountId, WORKSPACE_SWITCH_SECTIONS, workspaceId, options)
  }

  public async bootInitialWorkspace(
    accountId: number,
    workspaceId: number | string,
    options?: BootstrapFetchOptions & { includeCompositions?: boolean },
  ): Promise<MappedBootstrapResult> {
    const sections = options?.includeCompositions
      ? INITIAL_WORKSPACE_WITH_COMPOSITIONS_SECTIONS
      : INITIAL_WORKSPACE_SECTIONS

    return this.fetch(accountId, sections, workspaceId, options)
  }

  public async bootInitialFull(
    accountId: number,
    workspaceId: number | string,
    options?: BootstrapFetchOptions & { includeCompositions?: boolean },
  ): Promise<MappedBootstrapResult> {
    const sections = options?.includeCompositions
      ? INITIAL_FULL_WITH_COMPOSITIONS_SECTIONS
      : INITIAL_FULL_SECTIONS

    return this.fetch(accountId, sections, workspaceId, options)
  }

  public async bootCompositionsOnly(
    accountId: number,
    workspaceId: number | string,
    options?: BootstrapFetchOptions,
  ): Promise<MappedBootstrapResult> {
    return this.fetch(accountId, ['compositions'], workspaceId, options)
  }

  /** The workspace-attribute re-hydrate behind `refreshAttributes()` (CM-22-01). Boot already
   *  sources attributes from the `settings` section, so a write re-reads that same section rather
   *  than the legacy `settings/key-event/list/` endpoint — one wire shape, one mapper, no second
   *  source of truth for the blob. */
  public async bootSettingsOnly(
    accountId: number,
    workspaceId: number | string,
    options?: BootstrapFetchOptions,
  ): Promise<MappedBootstrapResult> {
    return this.fetch(accountId, ['settings'], workspaceId, options)
  }

  /**
   * An ARBITRARY section set — the one caller that cannot use a preset (ORP-20409). A degraded
   * boot leaves a `{section, retryable}` record whose shape is decided by the server, so the retry
   * asks for exactly those sections and nothing else. Re-asking a whole preset would refetch
   * sections that already landed, which is the cost the partial retry exists to avoid.
   *
   * Scope is the CALLER's business: `feature_flags` and the other workspace-scoped sections 400 the
   * whole request without a `workspace_id` (backend `WORKSPACE_REQUIRED_SECTIONS`), so
   * `RetryDegradedSectionsFlow` filters them out when no workspace is selected rather than this
   * method guessing.
   */
  public async bootSections(
    accountId: number,
    sections: BootstrapSection[],
    workspaceId?: number | string,
    options?: BootstrapFetchOptions,
  ): Promise<MappedBootstrapResult> {
    return this.fetch(accountId, sections, workspaceId, options)
  }

  private async fetch(
    accountId: number,
    sections: BootstrapSection[],
    workspaceId?: number | string,
    options?: BootstrapFetchOptions,
  ): Promise<MappedBootstrapResult> {
    const response = await this.api.fetchBootstrap({
      accountId,
      sections,
      workspaceId,
      refresh: options?.refresh,
      // v4 resolves values lazily; non-dimensions requests keep their existing query shape.
      dropdownValues: sections.includes('dimensions') ? false : undefined,
      cursor: options?.cursor,
      scheduleId: options?.scheduleId,
    })
    return mapBootstrapResponse(response)
  }
}
